import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { errorMessage } from '@/lib/errors';
import { financeCategoriesQuery, useCreateFinanceAccount, useUpdateFinanceAccount } from '@/lib/finance';
import { CURRENCIES } from '@/lib/money';
import { AccountFeeRuleFields, draftToFeeRule, feeRuleToDraft, type FeeRuleDraft } from './AccountFeeRuleFields';
import type { FinanceAccount, FinanceFeeRule, FinanceAccountInput } from '@/api/generated/types.gen';

/**
 * The editable fields of an account, in one block: extend this type,
 * accountToForm and formToBody together.
 */
export interface AccountEditFormState {
  name: string;
  kind: FinanceAccountInput['kind'];
  currencies: string[];
  isActive: boolean;
  feeRule: FeeRuleDraft;
}

/** The create-mode form: active, one EUR, fee rule off. */
// eslint-disable-next-line react-refresh/only-export-components
export function emptyForm(): AccountEditFormState {
  return { name: '', kind: 'bank', currencies: ['EUR'], isActive: true, feeRule: feeRuleToDraft(null) };
}

// eslint-disable-next-line react-refresh/only-export-components
export function accountToForm(account: FinanceAccount): AccountEditFormState {
  return {
    name: account.name,
    kind: account.kind,
    currencies: [...account.currencies],
    isActive: account.is_active,
    feeRule: feeRuleToDraft(account.fee_rule),
  };
}

/**
 * PATCH /finance/accounts/{id} replaces the WHOLE account, so the fields this
 * form does not edit (sort, notes) travel as they are on the account. A
 * disabled fee rule travels as null: an all-zero rule is a 422.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function formToBody(
  account: FinanceAccount | undefined,
  form: AccountEditFormState,
  feeRule: FinanceFeeRule | null,
): FinanceAccountInput {
  return {
    name: form.name.trim(),
    kind: form.kind,
    currencies: form.currencies,
    is_active: form.isActive,
    ...(account ? { sort: account.sort } : {}),
    // The spec has fee_rule nullable, but the generator drops the null branch;
    // null is what clears the rule server-side (absent also would).
    fee_rule: feeRule as FinanceFeeRule | undefined,
    ...(account?.notes !== undefined ? { notes: account.notes } : {}),
  };
}

const KIND_OPTIONS = [
  { value: 'bank', label: 'Bank' },
  { value: 'cash', label: 'Cash' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'payoneer', label: 'Payoneer' },
  { value: 'other', label: 'Other' },
];

export interface AccountEditDialogProps {
  /** Absent = create mode: an empty form that POSTs a new account. */
  account?: FinanceAccount;
  onClose: () => void;
}

/**
 * Edit an account (name, kind, currencies, active). The server answers 409
 * when a currency is removed that has movements, a non-zero balance or an
 * opening balance; its message is shown inline and the form stays open.
 */
export function AccountEditDialog({ account, onClose }: AccountEditDialogProps) {
  const [form, setForm] = useState<AccountEditFormState>(() => (account ? accountToForm(account) : emptyForm()));
  const [failure, setFailure] = useState<string | null>(null);
  const [showRuleError, setShowRuleError] = useState(false);
  const update = useUpdateFinanceAccount();
  const create = useCreateFinanceAccount();
  const mut = account ? update : create;
  const categories = useQuery(financeCategoriesQuery());
  const rule = draftToFeeRule(form.feeRule);
  const ruleError = rule && 'error' in rule ? rule.error : undefined;

  const ready = form.name.trim().length > 0 && form.currencies.length > 0;
  // Offer the account's own currencies even if the shared table lacks one, so
  // an existing currency can always be seen and unticked.
  const offered = [...CURRENCIES, ...(account?.currencies ?? []).filter((c) => !CURRENCIES.includes(c))];

  return (
    <Dialog
      open
      onClose={onClose}
      title={account ? `Edit ${account.name}` : 'New account'}
      description={
        account
          ? 'Name, kind, currencies and whether the account is in use.'
          : 'A place money sits: a bank account, the cash box, a PayPal balance.'
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mut.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!ready}
            loading={mut.isPending}
            onClick={() => {
              setFailure(null);
              if (rule && 'error' in rule) {
                setShowRuleError(true);
                return;
              }
              const body = formToBody(account, form, rule);
              const handlers = { onSuccess: onClose, onError: (e: unknown) => setFailure(errorMessage(e)) };
              if (account) update.mutate({ id: account.id, body }, handlers);
              else create.mutate(body, handlers);
            }}
          >
            {account ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus />
        <Select
          label="Kind"
          value={form.kind}
          options={KIND_OPTIONS}
          onChange={(e) => setForm({ ...form, kind: e.target.value as FinanceAccountInput['kind'] })}
        />
        <fieldset>
          <legend className="mb-2 text-sm font-semibold text-[var(--text-heading)]">Currencies</legend>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
            {offered.map((c) => (
              <Checkbox
                key={c}
                label={c}
                checked={form.currencies.includes(c)}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    currencies: e.target.checked ? [...f.currencies, c] : f.currencies.filter((x) => x !== c),
                  }))
                }
              />
            ))}
          </div>
          {form.currencies.length === 0 && (
            <p className="mt-2 text-xs text-[var(--ollio-gold-deep)]">An account holds at least one currency.</p>
          )}
        </fieldset>
        <Checkbox
          label="Active"
          checked={form.isActive}
          onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
        />
        <AccountFeeRuleFields
          value={form.feeRule}
          onChange={(feeRule) => setForm({ ...form, feeRule })}
          categories={categories.data ?? []}
          error={showRuleError ? ruleError : undefined}
        />
        {failure && (
          <p role="alert" className="text-sm text-destructive">
            {failure}
          </p>
        )}
      </div>
    </Dialog>
  );
}
